import React, { useState } from 'react';
import { 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Button, 
  Paper,
  Typography,
  Box,
  IconButton,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import apiService from '../components/ReasuableFuncations/api';

const ApiKeyGenerator = () => {
  const [organization, setOrganization] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [showDocs, setShowDocs] = useState(false);

  const handleGenerateKey = async () => {
    try {
      const response = await apiService.post('/api/generate-api-key', { organization });
      const data = response.data;
      if (response.statusText !== 'Created') {
        throw new Error('Failed to generate API key');
      } 

        setApiKey(data.apiKey);
        setShowDocs(true);
        setCopySuccess(false);
    } catch (error) {
      console.error('Error generating API key:', error);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(apiKey);
    setCopySuccess(true);
  };

  const styles = {
    container: {
      maxWidth: '900px',
      margin: '0 auto',
      padding: '20px',
    },
    formControl: {
      margin: '10px 0',
      width: '50%',
    },
    codeBlock: {
      backgroundColor: '#f5f5f5',
      padding: '10px',
      borderRadius: '5px',
      fontFamily: 'monospace',
      overflowX: 'auto',
    },
    section: {
      marginBottom: '24px',
    }
  };

  const baseUrl = 'https://comesa-backend.onrender.com';

  return (
    <Paper style={styles.container}>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        style={{ 
          maxWidth: '600px',
          margin: '0 auto',
          padding: '20px'
        }}
      >
        {/* Previous generate key UI remains the same */}
        <Typography variant="h4" gutterBottom align="center">
          Generate API Key
        </Typography>

        <FormControl style={{ margin: '20px 0', width: '100%', maxWidth: '400px' }}>
          <InputLabel id="organization">Organization</InputLabel>
          <Select
            label="organization"
            value={organization}
            onChange={(e) => setOrganization(e.target.value)}
            fullWidth
          >
            <MenuItem value="COMESA">COMESA</MenuItem>
            <MenuItem value="EAC">EAC</MenuItem>
            <MenuItem value="IGAD">IGAD</MenuItem>
            <MenuItem value="IOC">IOC</MenuItem>
            <MenuItem value="SADC">SADC</MenuItem>
          </Select>
        </FormControl>

        <Box mt={2} textAlign="center">
          <Button
            variant="contained"
            color="primary"
            onClick={handleGenerateKey}
          >
            Generate API Key
          </Button>
        </Box>

        {apiKey && (
          <Box 
            mt={2} 
            p={2} 
            bgcolor="#f5f5f5" 
            borderRadius={1}
            maxWidth="100%"
            textAlign="center"
          >
            <Typography variant="h6" align="center">Your API Key:</Typography>
            <Box 
              display="flex" 
              alignItems="center" 
              justifyContent="center"
              flexWrap="wrap"
              gap={1}
            >
              <Typography 
                variant="body1" 
                style={{ 
                  fontFamily: 'monospace', 
                  wordBreak: 'break-all',
                  maxWidth: '100%'
                }}
              >
                {apiKey}
              </Typography>
              <IconButton onClick={handleCopy} size="small">
                <ContentCopyIcon />
              </IconButton>
            </Box>
            {copySuccess && (
              <Typography color="success.main" variant="body2" align="center">
                Copied to clipboard!
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {showDocs && (
        <Box mt={4}>
          <Typography variant="h4" gutterBottom>Project Data API Documentation</Typography>

          <Box style={styles.section}>
            <Typography variant="h5" gutterBottom>Base URL</Typography>
            <Box style={styles.codeBlock}>
              {baseUrl}
            </Box>
          </Box>

          <Box style={styles.section}>
            <Typography variant="h5" gutterBottom>Authentication</Typography>
            <Typography>Add your API key as a query parameter to all requests:</Typography>
            <Box style={styles.codeBlock}>
              ?apiKey=your_api_key_here
            </Box>
          </Box>

          <Box style={styles.section}>
            <Typography variant="h5" gutterBottom>Available Endpoints</Typography>
            
            <Box mb={2}>
              <Typography variant="h6" gutterBottom>1. Get All Projects</Typography>
              <Box style={styles.codeBlock}>
                GET {baseUrl}/api?apiKey=your_api_key_here
              </Box>
            </Box>

            <Box mb={2}>
              <Typography variant="h6" gutterBottom>2. Get Project by ID</Typography>
              <Box style={styles.codeBlock}>
                GET {baseUrl}/api/:id?apiKey=your_api_key_here
              </Box>
            </Box>

            <Box mb={2}>
              <Typography variant="h6" gutterBottom>3. Get Projects by Status</Typography>
              <Box style={styles.codeBlock}>
                GET {baseUrl}/api/status/:status?apiKey=your_api_key_here
              </Box>
              <Typography variant="body1" mt={1}>Valid status values:</Typography>
              <ul>
                <li>Pending</li>
                <li>Ongoing</li>
                <li>Completed</li>
              </ul>
            </Box>

            <Box mb={2}>
              <Typography variant="h6" gutterBottom>4. Get Projects by Climate Type</Typography>
              <Box style={styles.codeBlock}>
                GET {baseUrl}/api/climate/:type?apiKey=your_api_key_here
              </Box>
              <Typography variant="body1" mt={1}>Valid climate types:</Typography>
              <ul>
                <li>GREENHOUSE_GAS_EMISSIONS</li>
                <li>ENERGY_CONSUMPTION</li>
                <li>SUSTAINABLE_PRACTICES</li>
                <li>CLIMATE_EDUCATION_AND_AWARENESS</li>
              </ul>
            </Box>
          </Box>

          <Box style={styles.section}>
            <Typography variant="h5" gutterBottom>Example Request</Typography>
            <Typography variant="body1" gutterBottom>To get all projects:</Typography>
            <Box style={styles.codeBlock}>
              {`${baseUrl}/api?apiKey=${apiKey}`}
            </Box>
          </Box>

          <Box style={styles.section}>
            <Typography variant="h5" gutterBottom>Error Responses</Typography>
            <Typography>All error responses follow this format:</Typography>
            <Box style={styles.codeBlock}>
              {`{
  "error": "Error message here"
}`}
            </Box>
            <Typography variant="body1" mt={1}>Common Status Codes:</Typography>
            <ul>
              <li>200: Successful request</li>
              <li>401: Invalid or missing API key</li>
              <li>404: Resource not found</li>
              <li>429: Rate limit exceeded</li>
              <li>500: Server error</li>
            </ul>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default ApiKeyGenerator;